import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, BACKEND_URL } from "../lib/api";
import { Stack, LinkSimple, FilePdf, FileCsv } from "@phosphor-icons/react";

const ACTION_COLORS = {
  EVENT_INITIALIZED: "text-[#00FF66]",
  INVENTORY_ALLOCATED: "text-[#FF9900]",
  TICKET_DISTRIBUTED: "text-[#FF0033]",
  TICKET_PAID: "text-[#00FF66]",
  TICKET_REDEEMED: "text-[#9D00FF]",
  USER_REGISTERED: "text-white",
};

export default function Ledger() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ledger?limit=200");
        setEntries(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AppShell>
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Cryptographic Ledger · Append-Only</div>
          <h1 className="heading-font text-4xl sm:text-5xl font-black tracking-tighter mt-2">Audit Ledger</h1>
          <p className="mono-font text-xs text-white/50 mt-3 max-w-2xl">
            SHA-256 chained blocks. Each block cryptographically references the previous, forming an immutable trail of every
            system event across the tenant.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${BACKEND_URL}/api/ledger.csv`} target="_blank" rel="noreferrer"
             data-testid="ledger-csv-download"
             className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-[0.2em] hover:border-[#00FF66] hover:text-[#00FF66] transition-colors duration-200">
            <FileCsv size={14} /> Ledger CSV
          </a>
          <a href={`${BACKEND_URL}/api/ledger.pdf`} target="_blank" rel="noreferrer"
             data-testid="ledger-pdf-download"
             className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 mono-font text-[10px] uppercase tracking-[0.2em] hover:border-[#00FF66] hover:text-[#00FF66] transition-colors duration-200">
            <FilePdf size={14} /> Ledger PDF
          </a>
        </div>
      </div>
      {loading ? (
        <div className="mono-font text-sm text-white/40 py-16 text-center">Loading ledger…</div>
      ) : entries.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center">
          <Stack size={40} weight="duotone" className="mx-auto text-white/40" />
          <div className="mono-font text-xs text-white/50 mt-4">Genesis awaits — no blocks yet.</div>
        </div>
      ) : (
        <div className="border border-white/10 border-glow-green overflow-x-auto">
          <table className="terminal-table" data-testid="ledger-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Details</th>
                <th>Prev Hash</th>
                <th>Block Hash</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((b) => (
                <tr key={b.index} data-testid={`ledger-row-${b.index}`}>
                  <td className="text-white/60">{String(b.index).padStart(4, "0")}</td>
                  <td className="text-white/60">{new Date(b.timestamp).toLocaleString()}</td>
                  <td className={ACTION_COLORS[b.action] || "text-white"}>{b.action}</td>
                  <td className="text-white/80 max-w-md truncate">{b.details}</td>
                  <td className="text-white/40">{b.prev_hash === "GENESIS" ? "GENESIS" : b.prev_hash.slice(0, 12) + "…"}</td>
                  <td className="text-[#00FF66]">
                    <span className="inline-flex items-center gap-1">
                      <LinkSimple size={11} />
                      {b.block_hash.slice(0, 12)}…
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
